import React, { useEffect, useMemo, useRef, useState } from "react";
import { PlusCircle, Paperclip, Check, X, Upload, Layers, CheckSquare, Printer } from "react-feather";
import toast from "react-hot-toast";
import DataTable from "../../../components/common/DataTable";
import StatusBadge from "../../../components/common/StatusBadge";
import InfoTile from "../../../components/common/InfoTile";
import AttachmentPreviewModal from "../../../components/common/AttachmentPreviewModal";
import DateRangePicker, { useDatePresetRange } from "../../../components/common/DateRangePicker";
import ListExportToolbar from "../../stock/components/list/ListExportToolbar";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";
import { useClaims } from "../../auth/useClaims";
import {
  useApproveFuelRefillMutation,
  useGetFuelCardsQuery,
  useGetFuelCardVehiclesQuery,
  useGetFuelVehiclesQuery,
  useLazyGetFuelRefillAttachmentQuery,
  useListFuelRefillsQuery,
  useRejectFuelRefillMutation,
  useUploadFuelRefillAttachmentMutation,
} from "../fuelApi";
import { useCanApproveFuelRefill, useCanCreateFuelRefill, useCanSelfApprove } from "../useFuelPermissions";
import RefillUsagePanel from "../components/RefillUsagePanel";
import { FUEL_REFILL_STATUS_OPTIONS, PAYMENT_MODE_OPTIONS, PaymentModeBadge, formatDate, money } from "../fuelFormat";
import FuelRefillCreateModal from "../components/FuelRefillCreateModal";
import FuelRefillByTypeModal from "../components/FuelRefillByTypeModal";
import FuelRefillBulkDecisionModal from "../components/FuelRefillBulkDecisionModal";
import FuelRefillDecisionModal from "../components/FuelRefillDecisionModal";
import { printFuelRefills } from "../components/FuelRefillPrint";

const PAGE_SIZE = 2000;

// Fuel Card Refills - money the office loads onto a prepaid card. NOT fuel
// purchases (those are Fuel Transactions, recorded by drivers in the app).
// SUBMITTED -> APPROVED | REJECTED; approval is the moment the balance moves,
// and the server stamps the card's balance before and after onto the refill.
const FuelRefillsPage = () => {
  const { selectedBranchId, availableBranches } = useBranchFilter();
  const { userId } = useClaims();
  const canCreate = useCanCreateFuelRefill();
  const canApprove = useCanApproveFuelRefill();
  const canSelfApprove = useCanSelfApprove();
  const { preset, setPreset, dateFrom, setDateFrom, dateTo, setDateTo } = useDatePresetRange("LAST_30_DAYS");
  const [status, setStatus] = useState("");
  const [fuelCardId, setFuelCardId] = useState("");
  const [paymentMode, setPaymentMode] = useState(""); // Batch200: "" = card + cash
  const [showCreate, setShowCreate] = useState(false);
  const [showByType, setShowByType] = useState(false);
  const [decision, setDecision] = useState(null); // { mode, refill }
  const [selectedKeys, setSelectedKeys] = useState([]); // recordGuids ticked for a group decision
  const [bulkMode, setBulkMode] = useState(null); // "approve" | "reject" | null
  const [preview, setPreview] = useState(null); // { blob, refill }
  const uploadInputRef = useRef(null);
  const uploadTargetRef = useRef(null);

  const { data: cards = [] } = useGetFuelCardsQuery({ branchId: selectedBranchId ?? undefined, includeInactive: true });
  // Which vehicles each card is assigned to (2026-09-22, "show the assigned vehicle reg
  // number after fuel card column"). A card can serve several vehicles, so the column lists
  // them all, default first. One read of the links, joined here by fuelCardId - the refill
  // rows carry only the card.
  const { data: cardVehicleLinks = [] } = useGetFuelCardVehiclesQuery({});
  const vehiclesByCardId = useMemo(() => {
    const map = new Map();
    [...cardVehicleLinks]
      .sort((a, b) => Number(b.isDefault) - Number(a.isDefault) || String(a.regNo).localeCompare(String(b.regNo)))
      .forEach((l) => {
        if (!map.has(l.fuelCardId)) map.set(l.fuelCardId, []);
        map.get(l.fuelCardId).push(l);
      });
    return map;
  }, [cardVehicleLinks]);
  // Who each vehicle belongs to (2026-10-09: "KDR 510 X shows down Sajeesh") - from the fuel
  // vehicle list, which already carries the allocated employee; shown under each reg no.
  const { data: fuelVehicles = [] } = useGetFuelVehiclesQuery({ includeInactive: true });
  const ownerByVehicleId = useMemo(
    () => new Map(fuelVehicles.filter((v) => v.allocatedToEmployeeName).map((v) => [v.vehicleId, v.allocatedToEmployeeName])),
    [fuelVehicles]
  );
  const Owner = ({ vehicleId }) =>
    ownerByVehicleId.get(vehicleId) ? <div className="tx-party" style={{ fontSize: 11, lineHeight: 1.2 }}>{ownerByVehicleId.get(vehicleId)}</div> : null;
  // A CASH refill (Batch200) names its vehicle directly; a CARD refill reaches its vehicles through the card.
  const vehicleRegsFor = (refill) =>
    refill.paymentMode === "CASH" ? [refill.regNo].filter(Boolean) : (vehiclesByCardId.get(refill.fuelCardId) || []).map((l) => l.regNo);
  const { data: refillsPage, isLoading, isFetching } = useListFuelRefillsQuery({
    branchId: selectedBranchId ?? undefined,
    fuelCardId: fuelCardId ? Number(fuelCardId) : undefined,
    status: status || undefined,
    paymentMode: paymentMode || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    pageSize: PAGE_SIZE,
  });
  const refills = refillsPage?.items || [];
  const totalCount = refillsPage?.totalCount ?? refills.length;
  const isTruncated = totalCount > refills.length;

  const [approveRefill, { isLoading: isApproving }] = useApproveFuelRefillMutation();
  const [rejectRefill, { isLoading: isRejecting }] = useRejectFuelRefillMutation();
  const [uploadAttachment] = useUploadFuelRefillAttachmentMutation();
  const [fetchAttachment] = useLazyGetFuelRefillAttachmentQuery();

  // "Is it mine" is decided on the user id, never the (first-name-only) display name.
  // Deciding your own refill additionally needs Admin.SelfApproval - the server enforces it.
  const canDecide = (refill) =>
    canApprove && refill.status === "SUBMITTED" && (refill.requestedByUserId !== userId || canSelfApprove);

  // Group decision (2026-09-30): tick the rows of a top-up sheet and approve or reject
  // them together. Only decidable rows can be ticked, and a tick is dropped as soon as
  // its row is no longer decidable (decided elsewhere, filter changed, list refreshed).
  const decidableGuids = useMemo(() => new Set(refills.filter(canDecide).map((r) => r.recordGuid)), [refills, canApprove, canSelfApprove, userId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setSelectedKeys((prev) => {
      const kept = prev.filter((k) => decidableGuids.has(k));
      return kept.length === prev.length ? prev : kept;
    });
  }, [decidableGuids]);
  const selectedRefills = refills.filter((r) => selectedKeys.includes(r.recordGuid));
  const selectedTotal = selectedRefills.reduce((sum, r) => sum + Number(r.refillAmount || 0), 0);
  const selectAllPending = () => setSelectedKeys([...decidableGuids]);

  const handleDecision = async (payload) => {
    const { mode, refill } = decision;
    try {
      if (mode === "approve") {
        await approveRefill({ recordGuid: refill.recordGuid, ...payload }).unwrap();
        toast.success(refill.paymentMode === "CASH" ? `${refill.refillNumber} approved - cash for ${refill.regNo}` : `${refill.refillNumber} approved - card balance updated`);
      } else {
        await rejectRefill({ recordGuid: refill.recordGuid, ...payload }).unwrap();
        toast.success(`${refill.refillNumber} rejected`);
      }
      setDecision(null);
    } catch (error) {
      toast.error(error?.message || "Could not save the decision");
    }
  };

  const handleViewAttachment = async (refill) => {
    try {
      const blob = await fetchAttachment(refill.recordGuid, true).unwrap();
      setPreview({ blob, refill });
    } catch (error) {
      toast.error(error?.message || "Could not open the document");
    }
  };

  const startUpload = (refill) => {
    uploadTargetRef.current = refill;
    uploadInputRef.current?.click();
  };
  const handleUploadPicked = async (e) => {
    const file = e.target.files?.[0];
    const refill = uploadTargetRef.current;
    e.target.value = "";
    if (!file || !refill) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("The document must be 2 MB or smaller");
      return;
    }
    try {
      await uploadAttachment({ recordGuid: refill.recordGuid, file }).unwrap();
      toast.success("Document attached");
    } catch (error) {
      toast.error(error?.message || "Could not attach the document");
    }
  };

  const approvedTotal = refills.filter((r) => r.status === "APPROVED").reduce((sum, r) => sum + Number(r.refillAmount || 0), 0);
  const approvedCashTotal = refills
    .filter((r) => r.status === "APPROVED" && r.paymentMode === "CASH")
    .reduce((sum, r) => sum + Number(r.refillAmount || 0), 0);
  const pending = refills.filter((r) => r.status === "SUBMITTED");
  const pendingTotal = pending.reduce((sum, r) => sum + Number(r.refillAmount || 0), 0);
  const branchCode = availableBranches.find((b) => b.branchId === selectedBranchId)?.branchCode;

  // Print (2026-10-28): one approved refill from its row, or every approved refill in the
  // current filter as one sheet - how a whole Top-up by Vehicle Type batch gets printed.
  const approvedRefills = refills.filter((r) => r.status === "APPROVED");
  const ownersFor = (refill) =>
    (refill.paymentMode === "CASH" ? [refill.vehicleId] : (vehiclesByCardId.get(refill.fuelCardId) || []).map((l) => l.vehicleId))
      .map((id) => ownerByVehicleId.get(id))
      .filter(Boolean);
  const handlePrint = (rows) => {
    const ok = printFuelRefills(rows, {
      branchCode: branchCode || rows[0]?.branchCode,
      vehicleRegsFor,
      ownersFor,
      subtitle: rows.length === 1 ? null : `Refill date ${dateFrom} to ${dateTo}`,
    });
    if (!ok) toast.error("Allow pop-ups for this site to print");
  };

  const columns = [
    {
      title: "Fuel card",
      dataIndex: "cardNumber",
      render: (v, r) =>
        r.paymentMode === "CASH" ? (
          <>
            <PaymentModeBadge mode="CASH" />
            <div className="small text-muted">{r.refillNumber} · {r.branchCode}</div>
          </>
        ) : (
          <>
            <span className="fw-semibold">{v}</span>
            <div className="small text-muted">{r.refillNumber} · {r.provider} · {r.branchCode}</div>
          </>
        ),
    },
    {
      title: "Vehicle",
      dataIndex: "fuelCardId",
      render: (fuelCardId, r) => {
        if (r.paymentMode === "CASH") return <><div className="fw-semibold">{r.regNo}</div><Owner vehicleId={r.vehicleId} /></>;
        const links = vehiclesByCardId.get(fuelCardId) || [];
        if (links.length === 0) return <span className="text-muted">no vehicle assigned</span>;
        return links.map((l) => (
          <div key={l.vehicleId} className={l.isDefault ? "fw-semibold" : ""} title={`${l.brand} ${l.model}${l.isDefault ? " · default card for this vehicle" : ""}`}>
            {l.regNo}
            <Owner vehicleId={l.vehicleId} />
          </div>
        ));
      },
    },
    { title: "Refill date", dataIndex: "refillDate", render: (v) => formatDate(v) },
    { title: "Amount", dataIndex: "refillAmount", align: "right", render: (v) => <span className="fw-semibold">{money(v)}</span> },
    { title: "Reference", dataIndex: "referenceNumber", render: (v) => v || "-" },
    { title: "Status", dataIndex: "status", render: (v) => <StatusBadge status={v} /> },
    { title: "Requested by", dataIndex: "requestedByName", render: (v, r) => (
      <>
        {v || "-"}
        <div className="small text-muted">{formatDate(r.createdDate)}</div>
      </>
    ) },
    {
      title: "Decided by",
      dataIndex: "approvedByName",
      render: (_, r) => {
        if (r.status === "APPROVED") {
          return (
            <>
              {r.approvedByName || "-"}
              <div className="small text-muted">{formatDate(r.approvedDate)}{r.approvalComment ? ` · ${r.approvalComment}` : ""}</div>
            </>
          );
        }
        if (r.status === "REJECTED") {
          return (
            <>
              {r.rejectedByName || "-"}
              <div className="small text-muted">{formatDate(r.rejectedDate)}{r.rejectionReason ? ` · ${r.rejectionReason}` : ""}</div>
            </>
          );
        }
        return <span className="text-muted">awaiting approval</span>;
      },
    },
    {
      title: "Card balance",
      dataIndex: "newBalance",
      render: (_, r) =>
        r.paymentMode === "CASH" ? (
          <span className="text-muted small">cash - no card</span>
        ) : r.status === "APPROVED" && r.previousBalance != null ? (
          <span className="text-nowrap">{money(r.previousBalance)} → <b>{money(r.newBalance)}</b></span>
        ) : r.status === "SUBMITTED" ? (
          <span className="text-muted text-nowrap">now {money(r.cardCurrentBalance)}</span>
        ) : (
          "-"
        ),
    },
    {
      title: "Document",
      dataIndex: "hasAttachment",
      render: (v, r) =>
        v ? (
          <button type="button" className="btn btn-link p-1" title={r.attachmentFileName || "View document"} onClick={() => handleViewAttachment(r)}>
            <Paperclip size={15} />
          </button>
        ) : canCreate && r.status === "SUBMITTED" ? (
          <button type="button" className="btn btn-link p-1 text-muted" title="Attach a supporting document" onClick={() => startUpload(r)}>
            <Upload size={15} />
          </button>
        ) : (
          "-"
        ),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_, r) =>
        canDecide(r) ? (
          <div className="d-flex gap-1">
            <button type="button" className="btn btn-sm btn-success d-inline-flex align-items-center gap-1" onClick={() => setDecision({ mode: "approve", refill: r })}>
              <Check size={14} /> Approve
            </button>
            <button type="button" className="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-1" onClick={() => setDecision({ mode: "reject", refill: r })}>
              <X size={14} /> Reject
            </button>
          </div>
        ) : r.status === "APPROVED" ? (
          <button type="button" className="btn btn-link p-1" title="Print this refill" onClick={() => handlePrint([r])}>
            <Printer size={15} />
          </button>
        ) : null,
    },
  ];

  const exportColumns = [
    { header: "Refill No", value: (r) => r.refillNumber },
    { header: "Payment", value: (r) => (r.paymentMode === "CASH" ? "Cash" : "Card") },
    { header: "Card", value: (r) => r.cardNumber || "" },
    { header: "Vehicle", value: (r) => vehicleRegsFor(r).join(", ") },
    {
      header: "Assigned to",
      value: (r) =>
        (r.paymentMode === "CASH" ? [r.vehicleId] : (vehiclesByCardId.get(r.fuelCardId) || []).map((l) => l.vehicleId))
          .map((id) => ownerByVehicleId.get(id))
          .filter(Boolean)
          .join(", "),
    },
    { header: "Provider", value: (r) => r.provider || "" },
    { header: "Branch", value: (r) => r.branchCode },
    { header: "Refill Date", value: (r) => formatDate(r.refillDate) },
    { header: "Reference", value: (r) => r.referenceNumber || "" },
    { header: "Status", value: (r) => r.status },
    { header: "Requested By", value: (r) => r.requestedByName || "" },
    { header: "Decided By", value: (r) => r.approvedByName || r.rejectedByName || "" },
    { header: "Amount", value: (r) => money(r.refillAmount), align: "right" },
    { header: "Balance Before", value: (r) => (r.previousBalance == null ? "" : money(r.previousBalance)), align: "right" },
    { header: "Balance After", value: (r) => (r.newBalance == null ? "" : money(r.newBalance)), align: "right" },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Fuel Card Refills</h4>
              <h6>Money loaded onto fuel cards, or handed over as cash for a vehicle&apos;s fuel - not fuel purchases. A card&apos;s balance goes up when its refill is approved</h6>
            </div>
          </div>
          {canCreate && (
            <div className="page-btn d-flex gap-2">
              {/* The monthly top-up sheet is one line per card of a vehicle type (2026-09-30):
                  this enters the whole sheet at once instead of one refill at a time. */}
              <button type="button" className="btn btn-outline-primary" onClick={() => setShowByType(true)}>
                <Layers className="me-2" size={16} />
                Top-up by Vehicle Type
              </button>
              <button type="button" className="btn btn-added" onClick={() => setShowCreate(true)}>
                <PlusCircle className="me-2" size={16} />
                New Refill
              </button>
            </div>
          )}
        </div>

        <div className="row g-2 mb-3">
          <div className="col-6 col-md-3"><InfoTile label="Refills" value={refills.length} /></div>
          <div className="col-6 col-md-3"><InfoTile label="Approved card refills" tone="success" value={money(approvedTotal - approvedCashTotal)} /></div>
          <div className="col-6 col-md-3"><InfoTile label="Approved cash" tone="success" value={money(approvedCashTotal)} /></div>
          <div className="col-6 col-md-3">
            <InfoTile label="Awaiting approval" tone={pending.length ? "maroon" : "neutral"}>
              {pending.length}
              <span className="text-muted ms-1 fw-normal" style={{ fontSize: 11 }}>({money(pendingTotal)})</span>
            </InfoTile>
          </div>
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="d-flex flex-column gap-2">
              <div className="d-flex justify-content-start">
                <DateRangePicker preset={preset} setPreset={setPreset} dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} />
              </div>
              <div className="table-top flex-wrap gap-2" style={{ rowGap: 8 }}>
                <div className="d-flex gap-2 align-items-center flex-wrap">
                  <select className="form-control form-control-sm" style={{ width: 240, height: 34 }} value={fuelCardId} onChange={(e) => setFuelCardId(e.target.value)}>
                    <option value="">All Cards</option>
                    {cards.map((c) => (
                      <option key={c.fuelCardId} value={c.fuelCardId}>{c.cardNumber} - {c.provider}</option>
                    ))}
                  </select>
                  <select className="form-control form-control-sm" style={{ width: 140, height: 34 }} value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)}>
                    {PAYMENT_MODE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <select className="form-control form-control-sm" style={{ width: 160, height: 34 }} value={status} onChange={(e) => setStatus(e.target.value)}>
                    {FUEL_REFILL_STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1 ms-auto"
                  disabled={approvedRefills.length === 0}
                  title="Print every approved refill in this list as one sheet"
                  onClick={() => handlePrint(approvedRefills)}
                >
                  <Printer size={14} /> Print approved ({approvedRefills.length})
                </button>
                <ListExportToolbar
                  title="Fuel Card Refills"
                  subtitleLines={[`Branch: ${branchCode || "All"}`, `Date: ${dateFrom} to ${dateTo}`]}
                  branchCode={branchCode}
                  columns={exportColumns}
                  rows={refills}
                  fileName="FuelCardRefills"
                  orientation="landscape"
                />
              </div>
            </div>

            {isTruncated && (
              <div className="alert alert-warning py-2 px-3 mb-2 mt-2">
                Showing the first {refills.length} of {totalCount} matching refills - narrow the date range or filters for a complete list.
              </div>
            )}

            {canApprove && decidableGuids.size > 0 && (
              <div
                className="d-flex flex-wrap align-items-center gap-2 mt-2 px-3 py-2 rounded"
                style={{ background: selectedKeys.length ? "#F1E4E7" : "#F8F9FA", border: "1px solid #E9ECEF", fontSize: 13 }}
              >
                <CheckSquare size={16} style={{ color: "#6E1423" }} />
                {selectedKeys.length ? (
                  <span>
                    <b>{selectedKeys.length}</b> of {decidableGuids.size} awaiting refill{decidableGuids.size === 1 ? "" : "s"} selected
                    <span className="text-muted"> · {money(selectedTotal)}</span>
                  </span>
                ) : (
                  <span className="text-muted">
                    Tick refills to approve or reject them together, or
                    <button type="button" className="btn btn-link btn-sm p-0 ms-1 align-baseline" onClick={selectAllPending}>
                      select all {decidableGuids.size} awaiting
                    </button>
                  </span>
                )}
                {selectedKeys.length > 0 && (
                  <div className="d-flex gap-1 ms-auto">
                    <button type="button" className="btn btn-sm btn-success d-inline-flex align-items-center gap-1" onClick={() => setBulkMode("approve")}>
                      <Check size={14} /> Approve selected
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-1" onClick={() => setBulkMode("reject")}>
                      <X size={14} /> Reject selected
                    </button>
                    <button type="button" className="btn btn-sm btn-light" onClick={() => setSelectedKeys([])}>
                      Clear
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="table-responsive mt-2">
              {/* Each refill opens into the card's usage since its previous refill (2026-09-22),
                  so an approver sees what the money went on before loading more. */}
              <DataTable
                columns={columns}
                dataSource={refills}
                rowKey="recordGuid"
                loading={isLoading || isFetching}
                expandable={{ expandedRowRender: (r) => <RefillUsagePanel refill={r} />, rowExpandable: (r) => r.paymentMode !== "CASH" }}
                rowSelection={
                  canApprove
                    ? {
                        selectedRowKeys: selectedKeys,
                        onChange: (keys) => setSelectedKeys(keys.filter((k) => decidableGuids.has(k))),
                        getCheckboxProps: (r) => ({ disabled: !decidableGuids.has(r.recordGuid) }),
                        preserveSelectedRowKeys: false,
                      }
                    : undefined
                }
              />
            </div>
          </div>
        </div>
      </div>

      <input ref={uploadInputRef} type="file" accept=".jpg,.jpeg,.png,.pdf" className="d-none" onChange={handleUploadPicked} />

      <FuelRefillCreateModal show={showCreate} onClose={() => setShowCreate(false)} />
      <FuelRefillByTypeModal show={showByType} onClose={() => setShowByType(false)} />
      <FuelRefillBulkDecisionModal
        show={Boolean(bulkMode)}
        mode={bulkMode}
        refills={selectedRefills}
        vehicleRegsFor={vehicleRegsFor}
        onClose={() => setBulkMode(null)}
        onDone={(doneGuids) => setSelectedKeys((prev) => prev.filter((k) => !doneGuids.includes(k)))}
      />
      <FuelRefillDecisionModal
        show={Boolean(decision)}
        mode={decision?.mode}
        refill={decision?.refill}
        onClose={() => setDecision(null)}
        onConfirm={handleDecision}
        isSaving={isApproving || isRejecting}
      />
      <AttachmentPreviewModal
        show={Boolean(preview)}
        onClose={() => setPreview(null)}
        blob={preview?.blob}
        fileName={preview?.refill.attachmentFileName}
        title={preview ? `${preview.refill.refillNumber} - supporting document` : ""}
      />
    </div>
  );
};

export default FuelRefillsPage;
